import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { SITE_EMAIL, SITE_NAME } from '@/constants/site';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const FORM_URL = 'https://readdy.ai/api/form/db73mfbaskivmpnrcfdg';

const values = [
  {
    icon: 'ri-database-2-line',
    title: '内容完整',
    desc: '覆盖电视剧与电影，剧情简介、演员阵容、更新动态一应俱全。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '高效筛选',
    desc: '按体裁、地区、年份与热度多维筛选，快速定位想看的内容。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '角色深读',
    desc: '核心角色专题分析，梳理人物关系与成长轨迹，读懂故事内核。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '全端适配',
    desc: '桌面与移动端均可流畅浏览，随时随地追剧查资料。',
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useDocumentMeta({
    title: '关于我们与联系方式 | 善良的女秘书',
    description:
      '了解善良的女秘书影视内容站的定位与内容体系，并通过留言表单与我们取得联系。本站专注影视剧剧情介绍、角色分析与资讯更新。',
    keywords: '关于我们,联系方式,善良的女秘书,影视内容站,留言表单',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      // Silently treat as success for bots; never send anything.
      setStatus('success');
      formEl.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      body.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 lg:px-12">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="whitespace-nowrap transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="whitespace-nowrap text-foreground-800">关于我们</span>
      </nav>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative h-48 w-full md:h-64">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20warm%20deep%20red%20editorial%20collage%20of%20film%20reels%20and%20soft%20light%2C%20elegant%20minimal%20background%2C%20magazine%20photography%20style%2C%20high%20detail%2C%20harmonious%20composition&width=1600&height=600&seq=about-cover&orientation=landscape"
            alt="关于善良的女秘书"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 to-foreground-950/25"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
            <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
              关于我们
            </h1>
            <p className="mt-2 max-w-xl text-sm text-background-200 md:text-base">
              {SITE_NAME} · 一个专注影视剧情介绍与角色解读的内容平台
            </p>
          </div>
        </div>

        <div className="p-6 md:p-10">
          <section className="max-w-3xl">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">
              网站定位
            </h2>
            <p className="mt-4 text-[15px] leading-loose text-foreground-700">
              善良的女秘书网站提供详尽的影视剧剧情介绍、角色分析及相关新闻更新，涵盖电视剧和电影等多种影视内容，帮助用户了解剧情发展与角色背景。我们希望通过清晰的信息组织与友好的浏览体验，让每一位喜爱影视剧、热衷于追剧的用户，都能更高效地获取所需内容。
            </p>
            <p className="mt-4 text-[15px] leading-loose text-foreground-700">
              本站内容以内容展示与学习交流为目的，所有影视作品简介、角色分析与资讯均为整理与演示用途，不提供任何在线播放或下载服务。
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">
              我们的特色
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${value.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {value.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-xl font-semibold text-foreground-950">
                联系方式
              </h2>
              <p className="mt-4 text-[15px] leading-loose text-foreground-700">
                如果你对我们的内容有任何建议、合作意向，或发现信息有误，欢迎通过留言表单与我们联系，我们会尽快回复。
              </p>
              <div className="mt-5 space-y-3">
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">电子邮箱</p>
                    <p className="text-sm text-foreground-900">{SITE_EMAIL}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">回复时间</p>
                    <p className="text-sm text-foreground-900">工作日 1-2 个工作日内回复</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">
                给我们留言
              </h2>
              <p className="mt-1.5 text-xs text-foreground-500">
                带 * 的为必填项，留言内容不超过 500 字。
              </p>

              {status === 'success' ? (
                <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-primary-300 bg-primary-50 px-6 py-10 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-3xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    提交成功，感谢你的留言！
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-500">
                    我们会尽快查看并与你联系。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-5 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="about-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-700">
                      你的称呼 *
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-700">
                      邮箱地址 *
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="请输入你的邮箱地址"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-700">
                      留言内容 *
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="请输入你的建议或问题（不超过 500 字）"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                    ></textarea>
                  </div>

                  <input
                    className="form-anti-bot-field"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === 'error' && (
                    <p className="rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-700">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className="ri-send-plane-line"></i>
                    {status === 'submitting' ? '提交中…' : '提交留言'}
                  </button>
                </form>
              )}
            </div>
          </section>

          <section className="mt-12 border-t border-background-200 pt-8">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">
              免责声明与版权声明
            </h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-500">
              <p>
                本站为非商业性质的影视内容介绍与资讯演示站点，所刊载的影视作品简介、角色分析及新闻资讯均为内容展示用途，仅供用户学习与交流参考，不构成任何官方信息发布。
              </p>
              <p>
                本站不提供任何影视作品的在线播放、下载或资源分发服务。所有作品的著作权归其各自权利人所有。如相关内容涉及版权问题，请通过上方联系方式与我们取得联系，我们将及时处理。
              </p>
              <p>
                © {new Date().getFullYear()} {SITE_NAME}. 保留所有权利。未经许可，不得以任何形式复制、转载本站原创内容。
              </p>
            </div>
            <Link
              to="/"
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}